import { type HandledError, type HandledErrorSeverity } from "./handled-error"

type Translate = (key: string, options?: Record<string, unknown>) => string

export interface PresentedHandledError {
  message: string
  description?: string
  type: HandledErrorSeverity
  actionLabel?: string
}

const ACTION_LABEL_KEYS = {
  retry: "errors.actions.retry",
  "restart-login": "errors.actions.restartLogin"
} as const

const OAUTH_HINT_KEY = "errors.oauth.continuationHint"

export const presentHandledError = (
  error: HandledError,
  t: Translate
): PresentedHandledError => {
  const fieldLines = (error.fieldErrors ?? []).map(({ field, message }) =>
    t("errors.fieldDetail", { field, message })
  )
  const descriptionParts = [
    error.code === "oauth_continuation" ? t(OAUTH_HINT_KEY) : undefined,
    error.detail,
    ...fieldLines
  ].filter((part): part is string => !!part)

  // An unclassified error has no title worth showing: "Something went wrong"
  // says nothing the user can act on, and burying the server's own sentence
  // underneath it is how a precise message ("This project type is already
  // assigned to the project.") reads as a crash. When the server explained
  // itself and we could not classify the failure, its sentence is the message.
  // Every other code has a title that adds something, so it keeps it.
  const leadWithDetail = error.code === "unknown" && !!error.detail

  const presented: PresentedHandledError = {
    message: leadWithDetail ? (error.detail as string) : t(error.messageKey),
    type: error.severity
  }

  const remaining = leadWithDetail
    ? descriptionParts.filter((part) => part !== error.detail)
    : descriptionParts

  if (remaining.length > 0) {
    presented.description = remaining.join("\n")
  }
  if (error.action) {
    presented.actionLabel = t(ACTION_LABEL_KEYS[error.action.kind])
  }

  return presented
}
